import React, { useEffect, useState, useMemo } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { getEmployeeList, selectApprovedEmployee } from '_redux/slices/timeline/employees';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { Colors } from '_styles';

const EmployeePicker = ({ selected, onChange }) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const employees = useSelector(selectApprovedEmployee) ?? [];

    const [show, setShow] = useState(false);
    const [draft, setDraft] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const handleOpen = async () => {
        setDraft(selected);
        setSearch('');
        if (employees.length === 0) {
            setLoading(true);
            const iata = dbUser?.details?.authority;
            if (iata) await dispatch(getEmployeeList(iata));
            setLoading(false);
        }
        setShow(true);
    };

    useEffect(() => {
        const iata = dbUser?.details?.authority;
        if (iata) dispatch(getEmployeeList(iata));
    }, []);

    const toggle = (employeeId) => {
        setDraft(prev =>
            prev.includes(employeeId)
                ? prev.filter(id => id !== employeeId)
                : [...prev, employeeId]
        );
    };

    const handleConfirm = () => {
        onChange(draft);
        setShow(false);
    };

    const filtered = useMemo(() =>
        employees.filter(e =>
            (e.title ?? '').toLowerCase().includes(search.toLowerCase()) ||
            (e.employeeId ?? '').toLowerCase().includes(search.toLowerCase())
        ),
        [employees, search]
    );

    const triggerLabel = selected.length === 0
        ? 'Select employees…'
        : `${selected.length} employee${selected.length === 1 ? '' : 's'} selected`;

    return (
        <>
            {/* ── Trigger ── */}
            <button
                type="button"
                onClick={handleOpen}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '10px 14px',
                    border: `1px solid ${selected.length > 0 ? Colors.PRM_LIGHT_BLUE : '#dee2e6'}`,
                    borderRadius: 8,
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 14,
                    color: selected.length > 0 ? Colors.PRM_LIGHT_BLUE : '#6b7280',
                    fontWeight: selected.length > 0 ? 600 : 400,
                    textAlign: 'left',
                    transition: 'border-color 0.15s',
                }}
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span style={{ flex: 1 }}>{triggerLabel}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {/* ── Modal ── */}
            <Modal show={show} onHide={() => setShow(false)} size="lg" centered scrollable>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '16px 20px' }}>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 700 }}>Assign Employees</Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: 0 }}>
                    {/* Search */}
                    <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6', position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                        <div style={{ position: 'relative' }}>
                            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <Form.Control
                                placeholder="Search by name or employee ID…"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ paddingLeft: 34, borderRadius: 8, fontSize: 14, borderColor: '#e5e7eb' }}
                                autoFocus
                            />
                        </div>
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: Colors.PRM_LIGHT_BLUE, fontWeight: 600 }}>
                            {draft.length === 0
                                ? 'No employees selected'
                                : `${draft.length} employee${draft.length === 1 ? '' : 's'} selected`}
                        </p>
                    </div>

                    {/* List */}
                    {loading ? (
                        <div style={{ padding: 40, textAlign: 'center' }}>
                            <Spinner animation="border" size="sm" style={{ color: Colors.PRM_LIGHT_BLUE }} />
                        </div>
                    ) : filtered.length === 0 ? (
                        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                            No employees found.
                        </div>
                    ) : (
                        filtered.map(employee => {
                            const checked = draft.includes(employee.employeeId);
                            const isOnline = employee.status === 'online';
                            return (
                                <div
                                    key={employee.employeeId}
                                    onClick={() => toggle(employee.employeeId)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 12,
                                        padding: '10px 20px',
                                        borderBottom: '1px solid #f9fafb',
                                        cursor: 'pointer',
                                        background: checked ? '#eff6ff' : '#fff',
                                        transition: 'background 0.1s',
                                    }}
                                >
                                    <div style={{
                                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                        backgroundColor: isOnline ? '#22c55e' : '#d1d5db',
                                    }} />
                                    <div style={{ flex: 1 }}>
                                        <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: '#111827' }}>
                                            {employee.title ?? ''}
                                        </p>
                                        <p style={{ margin: 0, fontSize: 11, color: '#9ca3af' }}>
                                            {employee.employeeId}
                                        </p>
                                    </div>
                                    <Form.Check
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(employee.employeeId)}
                                        onClick={e => e.stopPropagation()}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                </div>
                            );
                        })
                    )}
                </Modal.Body>

                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '12px 20px' }}>
                    <Button variant="outline-secondary" onClick={() => setShow(false)} style={{ borderRadius: 8 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        style={{
                            borderRadius: 8,
                            backgroundColor: Colors.PRM_LIGHT_BLUE,
                            border: 'none',
                            fontWeight: 600,
                            minWidth: 140,
                        }}
                    >
                        {draft.length === 0
                            ? 'Confirm (none)'
                            : `Select ${draft.length} employee${draft.length === 1 ? '' : 's'}`}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EmployeePicker;
